import { useRef } from 'react';
import { FormField, FormItem } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface ColorSelectorProps {
  form: UseFormReturn<any>;
  offerType: 'A' | 'B';
  colorsArray: Array<{ id?: string; name: string; value?: string | null; color?: string | null }>;
  colorFieldName: string;
  customColorFieldName?: string;
  label: string;
  customColorLabel?: string;
  noBorderLabel?: string;
  showNoBorder?: boolean;
  showCustomColor?: boolean;
  getFinalColor?: (colorType: string) => string;
  getStyle?: (colorId: string, customColor?: string) => React.CSSProperties;
  defaultValue?: string;
}

export default function ColorSelector({
  form,
  offerType,
  colorsArray,
  colorFieldName,
  customColorFieldName,
  label,
  customColorLabel: customColorLabelProp,
  noBorderLabel: noBorderLabelProp,
  showNoBorder = false,
  showCustomColor = true,
  getFinalColor,
  getStyle,
  defaultValue,
}: ColorSelectorProps) {
  const { t } = useTranslation('common');
  const customColorLabel = customColorLabelProp ?? t('colorSelector.customColor');
  const noBorderLabel = noBorderLabelProp ?? t('colorSelector.noBorder');
  const colorPickerRef = useRef<HTMLInputElement>(null);
  const currentValue = form.watch(colorFieldName);
  const isNoBorder = currentValue === 'no-border';
  const isSimpleColor = !getFinalColor && !getStyle;

  const displayColor = isSimpleColor
    ? currentValue || defaultValue || '#000000'
    : getFinalColor
      ? getFinalColor(colorFieldName)
      : '#000000';

  const displayStyle = isSimpleColor
    ? { backgroundColor: displayColor }
    : getStyle
      ? getStyle(
          currentValue || defaultValue || 'background',
          form.watch(customColorFieldName || ''),
        )
      : { backgroundColor: displayColor };

  return (
    <div className="flex items-center gap-4 relative">
      <label
        htmlFor={
          showCustomColor && customColorFieldName
            ? `${colorFieldName}-picker-${offerType}`
            : undefined
        }
        className="size-14 shrink-0 rounded-lg border border-input cursor-pointer relative"
        style={displayStyle}
      >
        {showCustomColor && customColorFieldName && (
          <input
            ref={colorPickerRef}
            type="color"
            value={displayColor}
            onChange={(e) => {
              form.setValue(colorFieldName, 'custom');
              if (customColorFieldName) {
                form.setValue(customColorFieldName, e.target.value);
              }
            }}
            className="sr-only absolute inset-0 w-full h-full opacity-0"
            id={`${colorFieldName}-picker-${offerType}`}
          />
        )}
        {isNoBorder && (
          <div
            className="w-full h-full flex items-center justify-center rounded-lg"
            style={{
              backgroundColor: 'white',
              position: 'relative',
            }}
          >
            <div
              className="w-full h-0.5 bg-black"
              style={{
                transform: 'rotate(135deg)',
              }}
            />
          </div>
        )}
      </label>
      <div className="w-full">
        <FormField
          name={colorFieldName}
          control={form.control}
          render={({ field }) => (
            <FormItem className="h-14">
              <Select
                value={
                  field.value ||
                  defaultValue ||
                  (showNoBorder ? 'no-border' : colorsArray[0]?.value || colorsArray[0]?.id || '')
                }
                onValueChange={(value) => {
                  field.onChange(value);
                  if (value !== 'custom' && value !== 'no-border' && customColorFieldName) {
                    form.setValue(customColorFieldName, '#000000');
                  } else if (value === 'custom' && showCustomColor) {
                    setTimeout(() => {
                      colorPickerRef.current?.click();
                    }, 0);
                  }
                }}
              >
                <SelectTrigger label={label} className="h-14">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {showNoBorder && <SelectItem value="no-border">{noBorderLabel}</SelectItem>}
                  {colorsArray.map((color, index) => (
                    <SelectItem
                      key={color.id || color.value || index}
                      value={color.id || color.value || ''}
                    >
                      {color.name}
                    </SelectItem>
                  ))}
                  {showCustomColor && (
                    <SelectItem
                      value="custom"
                      onPointerDown={(e) => {
                        if (currentValue === 'custom' || field.value === 'custom') {
                          e.preventDefault();
                          setTimeout(() => {
                            colorPickerRef.current?.click();
                          }, 150);
                        }
                      }}
                    >
                      {customColorLabel}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
